import { useFormatPenceFactory } from "$routes/_lib/use-format-pence";
import ValidationContext from "$routes/_lib/validation/validation-context";
import InputError from "$routes/patient/_lib/html-controls/input-error/input-error";
import React, { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import type { ValidationError } from "yup";
import { TRANSACTION_TOTAL } from "./formConstants";

type FormProps = {
  transactions?: { amountInPence?: number }[];
};

export const TotalError = ({ totalPayableInPence }) => {
  const { watch } = useFormContext<FormProps>();
  const { formatMessage } = useIntl();
  const formatPence = useFormatPenceFactory();
  const validationContext = React.useContext(ValidationContext);
  const [error, setError] = React.useState<ValidationError>();

  const transactions = watch("transactions");

  const transactionTotal = transactions.reduce(
    (acc, next) => acc + next.amountInPence,
    0,
  );

  React.useEffect(() => {
    try {
      validationContext.validationSchema.validateSyncAt("transactions", {
        transactions,
      });
      setError(null);
    } catch (e) {
      if (e.path === TRANSACTION_TOTAL) {
        setError(e);
      } else {
        setError(null);
      }
    }
  }, [transactions, validationContext]);

  const errorMessage =
    transactionTotal < totalPayableInPence
      ? formatMessage(
          {
            id: "invoicing.takePayment.payment.partialWarning",
            defaultMessage:
              "You are taking a partial payment. There will be {amount} outstanding.",
          },
          {
            amount: formatPence(totalPayableInPence - transactionTotal),
          },
        )
      : error?.message;

  const { meta, style } = useMemo(
    () => ({
      meta: { error: errorMessage, touched: !!errorMessage },
      style: { textAlign: "end" },
    }),
    [errorMessage],
  );

  return (
    <InputError
      meta={meta}
      style={style}
      data-testid="invoicing.takePayment.totalError"
    />
  );
};
